/**
 * Balloon.tsx
 *
 * Celebration balloon component. Appears below the header, above content.
 * Auto-dismisses after 5 seconds. Tap X to dismiss early.
 *
 * Tiers:
 *   bronze — #B87333 star, matching left accent
 *   silver — #9AA0A6 star, matching left accent
 *   gold   — #C9A227 star, matching left accent + Gold pill + optional subtext
 */

import { useEffect, useRef } from 'react';

export type CelebrationTier = 'bronze' | 'silver' | 'gold';

export interface BalloonProps {
  tier: CelebrationTier;
  message: string;
  /** Gold only — shown below the message line */
  subtext?: string;
  onDismiss: () => void;
  /** Auto-dismiss delay in ms — default 5000 */
  autoDismissMs?: number;
}

const TIER_STYLES: Record<CelebrationTier, { starFill: string; accentBorder: string }> = {
  bronze: { starFill: '#B87333', accentBorder: '#B87333' },
  silver: { starFill: '#9AA0A6', accentBorder: '#9AA0A6' },
  gold:   { starFill: '#C9A227', accentBorder: '#B8860B' },
};

function StarIcon({ fill }: { fill: string }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill={fill}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
    </svg>
  );
}

export function Balloon({
  tier,
  message,
  subtext,
  onDismiss,
  autoDismissMs = 5000,
}: BalloonProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { starFill, accentBorder } = TIER_STYLES[tier];
  const isGold = tier === 'gold';

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      onDismiss();
    }, autoDismissMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [autoDismissMs, onDismiss]);

  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
  const ariaLabel = `${tierLabel} star — ${message}`;

  return (
    <div
      role="status"
      aria-label={ariaLabel}
      aria-live="polite"
      style={{
        background: '#fff',
        border: '0.5px solid rgba(0,0,0,0.08)',
        borderLeft: `3px solid ${accentBorder}`,
        borderRadius: 12,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      {/* Left: star + content */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
        <StarIcon fill={starFill} />
        <div style={{ minWidth: 0 }}>
          {/* Title row — message + gold pill */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <p style={{
              fontSize: 14,
              fontWeight: 500,
              color: '#1e1e1e',
              margin: 0,
              lineHeight: 1.3,
              letterSpacing: -0.2,
            }}>
              {message}
            </p>
            {isGold && (
              <span style={{
                fontSize: 11,
                fontWeight: 500,
                color: '#B8860B',
                background: '#FEF9EC',
                border: '0.5px solid #E8C84A',
                borderRadius: 20,
                padding: '1px 7px',
                lineHeight: 1.6,
                flexShrink: 0,
              }}>
                Gold
              </span>
            )}
          </div>
          {/* Subtext — gold only */}
          {isGold && subtext && (
            <p style={{
              fontSize: 12,
              color: '#6B7280',
              margin: '2px 0 0',
              lineHeight: 1.4,
              letterSpacing: -0.1,
            }}>
              {subtext}
            </p>
          )}
        </div>
      </div>

      {/* Dismiss button — 44px tap target */}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: '#9CA3AF',
          flexShrink: 0,
          marginRight: -10,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
